import React from 'react';
import { CardCondition, CardVariant, CONDITION_INFO, VARIANT_INFO } from '../types/collection.ts';

interface ConditionBadgeProps {
  condition: CardCondition;
  variant?: CardVariant;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  showVariant?: boolean;
  className?: string;
}

export default function ConditionBadge({
  condition,
  variant,
  size = 'md',
  showLabel = true,
  showVariant = false,
  className = ''
}: ConditionBadgeProps) {
  const info = CONDITION_INFO[condition];
  const variantInfo = variant ? VARIANT_INFO[variant] : null;

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  return (
    <div className={`inline-flex items-center gap-1.5 ${className}`}>
      <span
        className={`inline-flex items-center justify-center font-semibold rounded ${sizeClasses[size]}`}
        style={{
          backgroundColor: `${info.color}20`,
          color: info.color,
          border: `1px solid ${info.color}40`
        }}
        title={info.description}
      >
        {showLabel ? info.label : info.abbrev}
      </span>

      {showVariant && variantInfo && variant !== 'normal' && (
        <span
          className={`inline-flex items-center gap-1 bg-gray-700 text-gray-200 rounded ${sizeClasses[size]}`}
          title={variantInfo.label}
        >
          <span>{variantInfo.icon}</span>
          {showLabel && <span>{variantInfo.label}</span>}
        </span>
      )}
    </div>
  );
}

interface ConditionSelectorProps {
  value: CardCondition;
  onChange: (condition: CardCondition) => void;
  className?: string;
}

export function ConditionSelector({ value, onChange, className = '' }: ConditionSelectorProps) {
  const conditions: CardCondition[] = [
    'mint',
    'near_mint',
    'lightly_played',
    'moderately_played',
    'heavily_played',
    'damaged'
  ];

  return (
    <div className={`grid grid-cols-3 gap-2 ${className}`}>
      {conditions.map(condition => {
        const info = CONDITION_INFO[condition];
        const isSelected = value === condition;

        return (
          <button
            key={condition}
            type="button"
            onClick={() => onChange(condition)}
            className={`
              px-3 py-2 rounded-lg text-sm font-medium transition-all
              ${isSelected
                ? 'ring-2 ring-offset-2 ring-offset-gray-900'
                : 'hover:bg-gray-700'
              }
            `}
            style={{
              backgroundColor: isSelected ? `${info.color}30` : 'transparent',
              color: isSelected ? info.color : '#9CA3AF',
              borderColor: isSelected ? info.color : '#374151',
              border: '1px solid',
              ringColor: info.color
            }}
            title={info.description}
          >
            <span className="block font-bold">{info.abbrev}</span>
            <span className="block text-xs opacity-80">{info.label}</span>
          </button>
        );
      })}
    </div>
  );
}

interface VariantSelectorProps {
  value: CardVariant;
  onChange: (variant: CardVariant) => void;
  className?: string;
}

export function VariantSelector({ value, onChange, className = '' }: VariantSelectorProps) {
  const variants: CardVariant[] = [
    'normal',
    'holofoil',
    'reverse_holofoil',
    'full_art',
    'special_art'
  ];

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {variants.map(variant => {
        const info = VARIANT_INFO[variant];
        const isSelected = value === variant;

        return (
          <button
            key={variant}
            type="button"
            onClick={() => onChange(variant)}
            className={`
              px-3 py-2 rounded-lg text-sm font-medium transition-all
              flex items-center gap-1.5
              ${isSelected
                ? 'bg-blue-600 text-white ring-2 ring-blue-400 ring-offset-2 ring-offset-gray-900'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-600'
              }
            `}
          >
            <span>{info.icon}</span>
            <span>{info.label}</span>
          </button>
        );
      })}
    </div>
  );
}

interface ConditionCompactBadgeProps {
  condition: CardCondition;
  quantity?: number;
  className?: string;
}

export function ConditionCompactBadge({
  condition,
  quantity,
  className = ''
}: ConditionCompactBadgeProps) {
  const info = CONDITION_INFO[condition];

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-bold px-1.5 py-0.5 rounded ${className}`}
      style={{
        backgroundColor: `${info.color}20`,
        color: info.color
      }}
      title={info.description}
    >
      {info.abbrev}
      {quantity !== undefined && quantity > 1 && (
        <span className="text-gray-400">×{quantity}</span>
      )}
    </span>
  );
}
